import { API_ORIGIN, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { BiasSource, BiasSurface, CoachingFinding } from '../lib/bias-guardrail'
import type { BiasReport } from '../lib/bias-guardrail'

const reportKey = (ids: string[], surface?: BiasSurface) =>
  ['bias-report', [...ids].sort().join(','), surface ?? ''] as const

export function useBiasReport(artefactIds: Array<string | null | undefined>, surface?: BiasSurface) {
  const ids = artefactIds.filter((id): id is string => Boolean(id))
  return useQuery({
    queryKey: reportKey(ids, surface),
    enabled: ids.length > 0,
    queryFn: async (): Promise<BiasReport> => {
      const params = new URLSearchParams()
      params.set('artefact_ids', ids.join(','))
      if (surface) params.set('surface', surface)
      const response = await authorizedFetch(`${API_ORIGIN}/api/bias/report/?${params}`)
      if (!response.ok) {
        throw new Error('Failed to load bias incidents')
      }
      return response.json()
    },
  })
}

export function useBiasReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      text: string
      surface: BiasSurface
      artefactId: string
      source?: BiasSource
    }): Promise<{ findings: CoachingFinding[]; rewritten: boolean }> => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/bias/review/`, {
        method: 'POST',
        body: JSON.stringify({
          text: payload.text,
          surface: payload.surface,
          artefact_id: payload.artefactId,
          source: payload.source ?? 'generate',
        }),
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error((error as { error?: string }).error || 'Bias check failed')
      }
      return response.json()
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bias-report'] })
      queryClient.invalidateQueries({
        queryKey: reportKey([variables.artefactId], variables.surface),
      })
    },
  })
}
